/**
 * Bans: Panel-PID-Bans, Known-PID-Suche für Offline-Bans und Engine/RCON-Bans.
 * Offline-Bans laufen bewusst über pid_ban_add, nicht über admin.banPlayer
 * (das braucht einen Online-Slot).
 *
 * Die alten PID-Whitelists (chat_admins/ban_admins) werden hier NICHT mehr
 * gepflegt — In-Game-Rechte laufen über „Administration → Ingame Admins".
 */

import { useSignal, useSignalEffect } from "@preact/signals";
import { api, activeServer, isAdmin } from "../store";
import { PageHead, Panel } from "../components";
import { PanelGrid, type PanelItem } from "../grid/PanelGrid";
import { toast } from "../toast";
import type { ApiResponse } from "../api";
import { t } from "../i18n";
import { navigate } from "../router";

interface PidBan { name: string; reason: string; by: string; ts: string }
interface Seen { pid: string; name: string; first: number; last: number; server: string }

export interface OfflinePidBanPayload {
  pid: string;
  name: string;
  reason: string;
}

/** PID-Ban-Erklärung (i18n; in DE/EN). Funktion statt const, damit sie der Sprache folgt. */
export function pidBanExplanation(): string {
  return t("bans.pidBanExplanation");
}

export function normalizePid(value: string): string {
  return value.replace(/\D/g, "");
}

export function formatSeenDate(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "—";
  return new Date(value * 1000).toLocaleDateString();
}

export function buildOfflinePidBanPayload(pid: string, name: string, reason: string): OfflinePidBanPayload | null {
  const cleanPid = normalizePid(pid);
  const cleanReason = reason.trim();
  if (!cleanPid || !cleanReason) return null;
  return { pid: cleanPid, name: name.trim(), reason: cleanReason };
}

export function offlinePidBanConfirmText(payload: OfflinePidBanPayload): string {
  const label = payload.name || payload.pid;
  return `${t("bans.confirmCreatePre")} ${label} / ${payload.pid}${t("bans.confirmCreatePost")}\n\n${pidBanExplanation()}\n\n${t("bans.reasonLabel")}: ${payload.reason}`;
}

export async function runConfirmedOfflinePidBan(opts: {
  player: Pick<Seen, "pid" | "name">;
  askReason: (message: string) => string | null;
  confirmFn: (message: string) => boolean;
  send: (payload: OfflinePidBanPayload) => Promise<void>;
}): Promise<boolean> {
  const pid = normalizePid(opts.player.pid);
  if (!pid) return false;
  const label = opts.player.name || pid;
  const reason = opts.askReason(`${t("bans.banReasonPrompt")} ${label} ${t("bans.banReasonPromptSuffix")}`);
  const payload = buildOfflinePidBanPayload(pid, opts.player.name, reason ?? "");
  if (!payload) return false;
  const ok = opts.confirmFn(offlinePidBanConfirmText(payload));
  if (!ok) return false;
  await opts.send(payload);
  return true;
}

export function Bans() {
  const admin = isAdmin();
  const pidBans = useSignal<Record<string, PidBan>>({});
  const npid = useSignal(""); const nname = useSignal(""); const nreason = useSignal("");
  const seen = useSignal<Seen[]>([]); const q = useSignal("");
  const ipRaw = useSignal(""); const ipField = useSignal("");
  const edit = useSignal(false);

  async function loadPid() {
    const r = await api.call<ApiResponse & { bans: Record<string, PidBan> }>("pid_bans");
    if (r.ok) pidBans.value = r.bans;
  }
  useSignalEffect(() => { void loadPid(); });

  async function addPid() {
    const payload = buildOfflinePidBanPayload(npid.value, nname.value, nreason.value);
    if (!payload) return toast(t("bans.pidReasonRequired"), "err");
    const r = await api.call<ApiResponse & { bans: Record<string, PidBan> }>("pid_ban_add", { ...payload });
    if (r.ok) { pidBans.value = r.bans; npid.value = nname.value = nreason.value = ""; toast(t("bans.pidBanned"), "ok"); }
    else toast(String(r.error ?? t("common.error")), "err");
  }
  async function rmPid(pid: string) {
    if (!confirm(`${t("bans.confirmRemovePidPre")} ${pid}${t("bans.confirmRemovePidPost")}`)) return;
    const r = await api.call<ApiResponse & { bans: Record<string, PidBan> }>("pid_ban_remove", { pid });
    if (r.ok) { pidBans.value = r.bans; toast(t("bans.pidUnbanned"), "ok"); }
    else toast(String(r.error ?? t("common.error")), "err");
  }
  async function searchSeen() {
    const r = await api.call<ApiResponse & { players: Seen[] }>("seen_players", { q: q.value, limit: 300 });
    if (r.ok) seen.value = r.players;
  }
  async function offlineBan(s: Seen) {
    const ran = await runConfirmedOfflinePidBan({
      player: s,
      askReason: (message) => prompt(message, ""),
      confirmFn: (message) => confirm(message),
      send: async (payload) => {
        const r = await api.call<ApiResponse & { bans: Record<string, PidBan> }>("pid_ban_add", { ...payload });
        if (!r.ok) throw new Error(String(r.error ?? t("common.error")));
        pidBans.value = r.bans;
      },
    });
    if (ran) toast(t("bans.offlineBanSet"), "ok");
  }
  async function exec(cmd: string): Promise<string> {
    const sv = activeServer.value;
    if (!sv) { toast(t("view.noServerSelected"), "err"); return ""; }
    const r = await api.call<ApiResponse & { raw: string }>("rcon", { server: sv, cmd });
    if (!r.ok) { toast(String(r.error ?? t("common.error")), "err"); return ""; }
    return r.raw ?? "";
  }

  const pidList = Object.entries(pidBans.value);

  const items: PanelItem[] = [
    { id: "pid-registry", title: t("bans.activePidBans"), defaultSpan: 12, render: () => (
      <Panel title={t("bans.activePidBans")} actions={<span class="badge">{t("bans.panelWorkerEnforced")}</span>}>
        <div class="ban-note">{pidBanExplanation()}</div>
        {admin && (
          <div class="ban-entry-row">
            <input class="input mono" placeholder="PID" value={npid.value} onInput={(e) => (npid.value = (e.target as HTMLInputElement).value)} />
            <input class="input" placeholder="Name" value={nname.value} onInput={(e) => (nname.value = (e.target as HTMLInputElement).value)} />
            <input class="input grow" placeholder={t("bans.reasonRequiredPh")} value={nreason.value} onInput={(e) => (nreason.value = (e.target as HTMLInputElement).value)} />
            <button class="btn danger" type="button" onClick={() => void addPid()}>PID Ban</button>
          </div>
        )}
        {pidList.length === 0 ? <div class="empty">{t("bans.noPidBans")}</div> : (
          <table class="score ban-table">
            <thead><tr><th>PID</th><th>Name</th><th>{t("field.reason")}</th><th>{t("bans.by")}</th><th class="num">{t("bans.created")}</th>{admin && <th class="num">{t("field.action")}</th>}</tr></thead>
            <tbody>
              {pidList.map(([pid, b]) => (
                <tr key={pid}>
                  <td class="mono accent-cell" data-label="PID">{pid}</td>
                  <td data-label="Name">{b.name || "—"}</td>
                  <td class="muted" data-label="Reason">{b.reason || "—"}</td>
                  <td class="muted" data-label="By">{b.by || "—"}</td>
                  <td class="num muted" data-label="Created">{b.ts ? new Date(b.ts).toLocaleDateString() : "—"}</td>
                  {admin && <td class="num" data-label="Action"><button class="mini-action danger" type="button" onClick={() => void rmPid(pid)}>{t("common.remove")}</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    ) },
    { id: "known-pids", title: t("bans.knownPids"), defaultSpan: 12, render: () => (
      <Panel title={t("bans.knownPidsOffline")} actions={<span class="badge">{seen.value.length} {t("bans.hitsSuffix")}</span>}>
        <div class="ban-note">{pidBanExplanation()} {t("bans.knownNote")}</div>
        <div class="ban-entry-row">
          <input class="input grow" placeholder={t("bans.searchNamePid")} value={q.value}
            onInput={(e) => (q.value = (e.target as HTMLInputElement).value)}
            onKeyDown={(e) => { if (e.key === "Enter") void searchSeen(); }} />
          <button class="btn" type="button" onClick={() => void searchSeen()}>{t("common.search")}</button>
        </div>
        {seen.value.length === 0 ? <div class="empty">{t("bans.noResults")}</div> : (
          <table class="score ban-table">
            <thead><tr><th>Name</th><th>PID</th><th>{t("playerIntel.lastServer")}</th><th class="num">{t("bans.lastSeenShort")}</th>{admin && <th class="num">{t("field.action")}</th>}</tr></thead>
            <tbody>
              {seen.value.map((s) => (
                <tr key={s.pid}>
                  <td data-label="Name">{s.name || "—"}</td>
                  <td class="mono accent-cell" data-label="PID">{s.pid}</td>
                  <td class="muted" data-label="Letzter Server">{s.server || "—"}</td>
                  <td class="num muted" data-label="Zuletzt">{formatSeenDate(s.last)}</td>
                  {admin && <td class="num" data-label="Action"><button class="mini-action danger" type="button" onClick={() => void offlineBan(s)}>PID Ban</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    ) },
    { id: "engine-ip", title: t("bans.engineRconIp"), defaultSpan: 6, render: () => (
      <Panel title={t("bans.engineRconBanIp")} actions={<button class="btn sm" type="button" onClick={async () => (ipRaw.value = await exec("admin.listBannedAddresses"))}>↻ {t("common.load")}</button>}>
        <div class="ban-note">{t("bans.engineNote")}</div>
        <div class="ban-entry-row">
          <input class="input mono grow" placeholder={t("bans.ipAddress")} value={ipField.value} onInput={(e) => (ipField.value = (e.target as HTMLInputElement).value)} />
          <button class="btn danger" type="button" onClick={async () => { if (ipField.value) { await exec(`admin.addAddressToBanList ${ipField.value} perm`); toast(t("bans.ipBanned"), "ok"); ipField.value = ""; } }}>{t("bans.ban")}</button>
          <button class="btn ghost" type="button" onClick={async () => { if (ipField.value) { await exec(`admin.removeAddressFromBanList ${ipField.value}`); toast(t("bans.ipUnbanned"), "ok"); ipField.value = ""; } }}>{t("bans.unban")}</button>
        </div>
        <pre class="mono ban-raw">{ipRaw.value || "—"}</pre>
      </Panel>
    ) },
    ...(admin ? [
      { id: "ingame-admins-hint", title: t("bans.ingameAdminsTitle"), defaultSpan: 6, render: () => (
        <Panel title={t("bans.ingameAdminsTitle")} actions={<button class="btn sm primary" type="button" onClick={() => navigate("ingame-admins")}>{t("bans.openIngameAdmins")}</button>}>
          <div class="ban-note">{t("bans.legacyWhitelistReplaced")}</div>
          <div class="caption" style="margin-top:6px">{t("bans.ingameAdminsHint")}</div>
        </Panel>
      ) },
    ] as PanelItem[] : []),
  ];

  return (
    <div class="view-enter">
      <PageHead title={t("nav.bans")} sub={t("bans.sub")}>
        <button class={`btn sm ${edit.value ? "primary" : ""}`} type="button" onClick={() => (edit.value = !edit.value)}>{edit.value ? t("dashboard.editDone") : t("dashboard.edit")}</button>
      </PageHead>
      <PanelGrid view="bans" items={items} edit={edit} />
    </div>
  );
}
